import { Pipe, PipeTransform } from '@angular/core';
import { DateTime } from 'luxon';

// Mapping of Windows time zone IDs to IANA time zones for USA
const windowsToIanaMap: { [key: string]: string } = {
    // Mainland USA
    'Eastern Standard Time': 'America/New_York',
    'Central Standard Time': 'America/Chicago',
    'Mountain Standard Time': 'America/Denver',
    'Pacific Standard Time': 'America/Los_Angeles',
    // Alaska
    'Alaskan Standard Time': 'America/Anchorage',
    // Hawaii
    'Hawaiian Standard Time': 'Pacific/Honolulu',
    // US Territories
    'Atlantic Standard Time': 'America/Puerto_Rico', // Puerto Rico
    'Chamorro Standard Time': 'Pacific/Guam', // Guam and Northern Mariana Islands
    'Samoa Standard Time': 'Pacific/Pago_Pago', // American Samoa
    // Additional US-specific time zones
    'US Eastern Standard Time': 'America/New_York', // Alias for Eastern Standard Time
    'US Mountain Standard Time': 'America/Phoenix', // Arizona (no DST)
    'Central America Standard Time': 'America/Regina', // Rare in US, used for some regions (no DST)
    'Aleutian Standard Time': 'America/Adak' // Aleutian Islands (Hawaii-Aleutian Time)
};

function getIanaTimeZone(windowsTimeZone: string): string {
    return windowsToIanaMap[windowsTimeZone] || windowsTimeZone; // Fallback to input if not found
}

@Pipe({
    name: 'luxonFormat',
    standalone: false
})
export class LuxonFormatPipe implements PipeTransform {
    transform(value: string | Date | DateTime | null, format: string, timeZone?: string): string {
        if (!value) {
            return '';
        }

        // Convert input to Luxon DateTime
        let dateTime: DateTime;
        if (DateTime.isDateTime(value)) {
            // Existing implementation: value is already a Luxon DateTime
            dateTime = value;
        } else if (typeof value === 'string') {
            // Assume ISO string in UTC (e.g., "2025-10-29T16:30:00Z")
            dateTime = DateTime.fromISO(value, { zone: 'utc' });
        } else if (value instanceof Date) {
            // Assume Date is UTC
            dateTime = DateTime.fromJSDate(value, { zone: 'utc' });
        } else {
            console.warn('Invalid date input:', value);
            return '';
        }

        // Validate DateTime
        if (!dateTime.isValid) {
            console.warn('Invalid Luxon DateTime:', dateTime.invalidReason);
            return '';
        }

        // Apply time zone conversion if provided
        if (timeZone) {
            try {
                const ianaTimeZone = getIanaTimeZone(timeZone);
                dateTime = dateTime.setZone(ianaTimeZone);
                if (!dateTime.isValid) {
                    console.warn(`Invalid time zone conversion for: ${timeZone}`);
                    return '';
                }
            } catch (error) {
                console.error(`Invalid time zone: ${timeZone}`, error);
                return '';
            }
        }

        // Format the date
        return dateTime.toFormat(format);
    }
}

